import { api, configureAuth, setAccessToken, unwrap } from './api';
import {
  resolveStoredSelectedTenant,
  useAuth,
  type CurrentUser,
  type ImpersonationState,
  type SelectedTenant,
} from './auth';
import { listTenants } from './tenants';

interface RefreshResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
  actor_type: 'tenant' | 'backoffice';
  tenant_id: string | null;
}

interface MeResponse {
  id: string;
  email: string;
  actor_type: 'tenant' | 'backoffice';
  tenant_id: string | null;
  last_active_tenant_id?: string | null;
  is_impersonation: boolean;
  real_actor_id: string | null;
  permissions: string[];
  display_name?: string | null;
  is_platform_admin?: boolean;
  status?: string;
  avatar_url?: string | null;
}

let inflight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await unwrap<RefreshResponse>(api.post('/auth/refresh'));
      setAccessToken(data.access_token);
      return data.access_token;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export async function fetchMe(): Promise<{
  user: CurrentUser;
  impersonation: ImpersonationState;
  actorType: 'tenant' | 'backoffice';
  lastActiveTenantId: string | null;
} | null> {
  try {
    const me = await unwrap<MeResponse>(api.get('/auth/me'));
    const user: CurrentUser = {
      id: me.id,
      principal_id: me.id,
      tenant_id: me.tenant_id,
      email: me.email,
      display_name: me.display_name ?? null,
      avatar_url: me.avatar_url ?? null,
      status: me.status ?? 'active',
      is_platform_admin: me.is_platform_admin ?? me.actor_type === 'backoffice',
      effective_permissions: me.permissions,
    };
    return {
      user,
      impersonation: {
        active: me.is_impersonation,
        real_user_id: me.real_actor_id,
      },
      actorType: me.actor_type,
      lastActiveTenantId: me.last_active_tenant_id ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Select (or clear) the active tenant and refresh the cached identity.
 *
 * Permissions are tenant-scoped: `/auth/me` only returns `effective_permissions`
 * for the tenant named in the `X-Tenant-Id` header. After switching tenants we
 * must re-fetch identity so the UI gates on the new tenant's permissions
 * instead of a stale (or cross-tenant) set.
 */
export async function applySelectedTenant(
  snapshot: SelectedTenant | null,
): Promise<void> {
  const auth = useAuth.getState();
  auth.setSelectedTenant(snapshot);
  if (!snapshot) return;
  // Remember the choice server-side so the next login lands here directly,
  // even from a different device. Best-effort: never block tenant selection.
  await api
    .post('/auth/active-tenant', { tenant_id: snapshot.tenant_id })
    .catch(() => undefined);
  const result = await fetchMe();
  if (result) auth.setUser(result.user, result.impersonation);
}

export async function loadSessionContext(accessToken: string): Promise<boolean> {
  setAccessToken(accessToken);
  const result = await fetchMe();
  if (!result) return false;
  const auth = useAuth.getState();
  auth.setUser(result.user, result.impersonation);

  if (result.actorType === 'tenant') {
    const tenants = await listTenants().catch(() => []);
    auth.setTenants(tenants);
    const selectedTenant = resolveStoredSelectedTenant(
      result.user.id,
      tenants,
      result.lastActiveTenantId,
    );
    await applySelectedTenant(selectedTenant);
    return true;
  }

  auth.setTenants([]);
  auth.setSelectedTenant(null);
  return true;
}

export async function bootstrap(): Promise<void> {
  configureAuth({
    refresh: refreshAccessToken,
    onAuthLost: () => useAuth.getState().clearSession(),
  });
  const token = await refreshAccessToken();
  if (!token) {
    useAuth.getState().markHydrated();
    return;
  }
  const loaded = await loadSessionContext(token);
  if (!loaded) {
    useAuth.getState().clearSession();
  }
}
